/*
 * Reconcile an account to its statement, like QuickBooks: tick what is on the statement until the difference is 0.00.
 * Deposits and payments are listed apart, and past reconciles show below; an account's latest can be undone.
 *
 * Nov 1: linked accounts are checked against the bank every month by the Steward (api books/auto-reconcile.ts). A month
 * that matches is reconciled by itself; one that is off shows here (and rings the bell) with a button that starts that
 * month's reconcile with the bank's number filled in.
 */
import { useState } from "react";
import { isOwed, type BankAccountRow, type BooksSetup, type TransactionRow } from "@steward/shared";
import { useSettings, useUpdateSettings } from "../../api/hooks";
import { useReconcile, useReconcileLook, useReconciles, useUndoReconcile } from "../../api/books";
import { useSession } from "../../app/session";
import { DataTable } from "../../components/DataTable";
import { Combobox, InputText } from "../../components/fields";
import { moneyExact, shortDate } from "../../components/format";
import { useToast } from "../../components/overlays";
import { Banner, Button, Card, EmptyState, StatusLabel, Switch } from "../../components/parts";
import { Tile, centsOf, errText } from "./common";

export function Reconcile({ setup }: { setup: BooksSetup }) {
  const { company } = useSession();
  const look = useReconcileLook();
  const finish = useReconcile();
  const history = useReconciles(company.id);
  const undo = useUndoReconcile();
  const toast = useToast();
  const bankish = setup.accounts.filter((a) => a.kind !== "cash");
  const [accountId, setAccountId] = useState(bankish[0]?.id ?? "");
  const [endDay, setEndDay] = useState("");
  const [ending, setEnding] = useState("");
  const [ticked, setTicked] = useState<Set<string>>(new Set());
  const start = look.data;
  const tx = start?.transactions ?? [];
  const tickedRows = tx.filter((t) => ticked.has(t.id));
  const deposits = tickedRows.filter((t) => t.amountCents > 0).reduce((a, t) => a + t.amountCents, 0);
  const payments = tickedRows.filter((t) => t.amountCents < 0).reduce((a, t) => a + t.amountCents, 0);
  const sum = (start?.beginCents ?? 0) + deposits + payments;
  const want = centsOf(ending);
  const diff = want === null ? null : want - sum;
  const account = setup.accounts.find((a) => a.id === accountId);
  const begin = async () => {
    try {
      const r = await look.mutateAsync({ accountId, endDay });
      setTicked(new Set(r.transactions.map((t) => t.id)));
    } catch (e) {
      toast(errText(e), { kind: "error" });
    }
  };
  if (bankish.length === 0)
    return (
      <Card>
        <EmptyState icon="building" title="Add a bank or card account first">
          Reconcile checks Books against the bank's monthly statement.
        </EmptyState>
      </Card>
    );
  const part = (title: string, rows: TransactionRow[]) => (
    <DataTable
      title={title}
      rows={rows}
      rowKey={(t) => t.id}
      selected={ticked}
      onSelect={(s) => {
        // The tick boxes of this list only change this list's rows.
        const others = [...ticked].filter((id) => !rows.some((r) => r.id === id));
        setTicked(new Set([...others, ...s]));
      }}
      empty={<EmptyState icon="check" title="None" />}
      columns={[
        { key: "d", header: "Day", sort: (t) => t.day, render: (t) => shortDate(t.day) },
        { key: "w", header: "What the bank says", render: (t) => t.description },
        {
          key: "m",
          header: "Amount",
          align: "right",
          sort: (t) => t.amountCents,
          render: (t) => <span className="num">{moneyExact(t.amountCents)}</span>,
        },
      ]}
    />
  );
  const fixMonth = (a: BankAccountRow) => {
    if (!a.check) return;
    setAccountId(a.id);
    setEndDay(a.check.endDay);
    setEnding((a.check.bankCents / 100).toFixed(2));
    look.reset();
    void look.mutateAsync({ accountId: a.id, endDay: a.check.endDay }).then(
      (r) => setTicked(new Set(r.transactions.map((t) => t.id))),
      (e: unknown) => toast(errText(e), { kind: "error" }),
    );
  };
  return (
    <div className="stack">
      <AutoCheck accounts={bankish} onFix={fixMonth} />
      <Card title="Reconcile an account">
        <p className="muted">
          Get the bank's statement. Pick its last day and type its ending balance, then tick what is on it until the difference is
          0.00.
        </p>
        <div className="form-grid">
          <Combobox
            label="Account"
            value={accountId}
            onChange={(v) => {
              setAccountId(v);
              look.reset();
            }}
            options={bankish.map((a) => ({
              value: a.id,
              label: a.name,
              sub: a.reconciledThrough ? `Reconciled through ${shortDate(a.reconciledThrough)}` : "Never reconciled",
            }))}
          />
          <InputText label="Statement's last day" type="date" value={endDay} onChange={(e) => setEndDay(e.target.value)} />
          <InputText
            label="Statement's ending balance"
            prefix="$"
            inputMode="decimal"
            value={ending}
            description={account && isOwed(account.kind) ? "What you owe on it is a minus amount" : undefined}
            onChange={(e) => setEnding(e.target.value)}
          />
        </div>
        <div className="row">
          <Button onClick={() => void begin()} loading={look.isPending} disabled={!accountId || !endDay}>
            Start
          </Button>
        </div>
      </Card>
      {start ? (
        <>
          <div className="tiles bk-rec-tiles">
            <Tile
              label="Beginning balance"
              value={moneyExact(start.beginCents)}
              hint={start.first ? "The opening balance" : `Reconciled on ${shortDate(start.from)}`}
            />
            <Tile
              label="Deposits ticked"
              value={moneyExact(deposits)}
              hint={`${String(tickedRows.filter((t) => t.amountCents > 0).length)} ticked`}
            />
            <Tile
              label="Payments ticked"
              value={moneyExact(-payments)}
              hint={`${String(tickedRows.filter((t) => t.amountCents < 0).length)} ticked`}
            />
            <Tile
              label="Difference"
              value={diff === null ? "..." : moneyExact(diff)}
              hint={diff === null ? "Type the ending balance" : diff === 0 ? "Ready to finish" : "Must be 0.00"}
              tone={diff === 0 ? "up" : diff === null ? undefined : "down"}
            />
          </div>
          {part(
            "Deposits",
            tx.filter((t) => t.amountCents > 0),
          )}
          {part(
            "Payments and charges",
            tx.filter((t) => t.amountCents < 0),
          )}
          <div className="row end">
            <Button
              disabled={diff !== 0}
              loading={finish.isPending}
              onClick={() =>
                void finish.mutateAsync({ accountId, endDay, endBalanceCents: want ?? 0, ids: [...ticked] }).then(
                  () => {
                    toast("Reconciled");
                    look.reset();
                    setEnding("");
                  },
                  (e: unknown) => toast(errText(e), { kind: "error" }),
                )
              }
            >
              Finish
            </Button>
          </div>
        </>
      ) : null}
      <Card title="Past reconciles" flush>
        {history.data?.reconciles.length === 0 ? (
          <p className="muted bk-pad">None yet.</p>
        ) : (
          <DataTable
            title="Past reconciles"
            rows={history.data?.reconciles ?? []}
            rowKey={(r) => r.id}
            loading={history.isLoading}
            columns={[
              { key: "a", header: "Account", render: (r) => setup.accounts.find((a) => a.id === r.accountId)?.name ?? "" },
              { key: "d", header: "Statement day", sort: (r) => r.endDay, render: (r) => shortDate(r.endDay) },
              {
                key: "e",
                header: "Ending balance",
                align: "right",
                render: (r) => <span className="num">{moneyExact(r.endCents)}</span>,
              },
              { key: "n", header: "Ticked", align: "right", render: (r) => <span className="num">{r.count}</span> },
              {
                key: "w",
                header: "By",
                render: (r) => (r.auto ? <StatusLabel status="informative">The Steward</StatusLabel> : "You"),
              },
              {
                key: "u",
                header: "",
                align: "right",
                render: (r) =>
                  r.undone ? (
                    <StatusLabel status="inactive">Undone</StatusLabel>
                  ) : r.latest ? (
                    <Button
                      size="small"
                      type="tertiary"
                      icon="undo"
                      onClick={() =>
                        void undo.mutateAsync(r.id).then(
                          () => toast("Reconcile undone"),
                          (e: unknown) => toast(errText(e), { kind: "error" }),
                        )
                      }
                    >
                      Undo
                    </Button>
                  ) : null,
              },
            ]}
          />
        )}
      </Card>
      {setup.lockDay ? <Banner type="notice">The books are closed through {shortDate(setup.lockDay)}.</Banner> : null}
    </div>
  );
}

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
const monthOf = (day: string) => `${MONTHS[Number(day.slice(5, 7)) - 1] ?? ""} ${day.slice(0, 4)}`;

/** Each linked account's last month end check against the bank, and the switch for it. */
function AutoCheck({ accounts, onFix }: { accounts: BankAccountRow[]; onFix: (a: BankAccountRow) => void }) {
  const { company } = useSession();
  const s = useSettings(company.id);
  const save = useUpdateSettings();
  const toast = useToast();
  const linked = accounts.filter((a) => a.linked && !a.personal);
  if (!s.data || linked.length === 0) return null;
  const on = s.data.settings.books.autoReconcile;
  return (
    <Card title="Checked against the bank each month">
      <div className="stack">
        <label className="row">
          <Switch
            checked={on}
            onChange={(v) =>
              save.mutate(
                { books: { autoReconcile: v } },
                { onSuccess: () => toast("Saved."), onError: (e) => toast(errText(e), { kind: "error" }) },
              )
            }
            label="Reconcile linked accounts by itself"
          />
          <span>
            Reconcile linked accounts by itself
            <span className="muted small" style={{ display: "block" }}>
              5 days after each month ends, the Steward checks the bank&apos;s balance against Books. If they match to the cent,
              the month is reconciled. If not, you get a note in the bell and it shows here.
            </span>
          </span>
        </label>
        {on
          ? linked.map((a) => {
              const c = a.check;
              const owed = isOwed(a.kind);
              const show = (v: number) => moneyExact(owed ? -v : v);
              return (
                <div key={a.id} className="row tight bk-auto-rec">
                  <b>{a.name}</b>
                  {!c ? (
                    <span className="muted">
                      {a.reconciledThrough
                        ? `Reconciled through ${shortDate(a.reconciledThrough)}`
                        : "First check after the next month ends"}
                    </span>
                  ) : c.ok ? (
                    <>
                      <StatusLabel status="success">Matches</StatusLabel>
                      <span className="muted">Matches the bank through {shortDate(c.endDay)}</span>
                    </>
                  ) : (
                    <>
                      <StatusLabel status="warning">{monthOf(c.endDay)} is off</StatusLabel>
                      <span>
                        Off by {moneyExact(Math.abs(c.bankCents - c.booksCents))}. The bank says {show(c.bankCents)}, Books says{" "}
                        {show(c.booksCents)}.
                      </span>
                      <Button size="small" type="secondary" onClick={() => onFix(a)}>
                        Reconcile {monthOf(c.endDay)}
                      </Button>
                    </>
                  )}
                </div>
              );
            })
          : null}
      </div>
    </Card>
  );
}
